<template>
  <div class="review-step">
    <!-- Client Info -->
    <div class="review-section">
      <h3 class="review-section__title">Client Info</h3>
      <themed-table
        :data="clientFieldsData"
        :columns="clientInfoColumns"
        :bordered="true"
        size="md"
      />
    </div>
    
    <!-- Subscription Plan -->
    <div class="review-section">
      <h3 class="review-section__title">Subscription Plan</h3>
      <div class="review-plan-card">
        <div class="review-plan-card__content">
          <div v-if="selectedPlanObj.product?.images?.[0]" class="review-plan-card__image">
            <img :src="selectedPlanObj.product.images[0]" :alt="selectedPlanObj.product.name || 'Plan' + ' image'" />
          </div>
          <div class="review-plan-card__info">
            <div class="review-plan-card__name">{{ selectedPlanObj.product?.name || 'N/A' }}</div>
            <div class="review-plan-card__description">{{ selectedPlanObj.product?.description || '' }}</div>
          </div>
        </div>
        <div class="review-plan-card__pricing">
          <span class="review-plan-card__price">
            {{ formatCurrencyCents(selectedPlanObj.unit_amount, selectedPlanObj.currency) }}
          </span>
          <span v-if="selectedPlanObj.recurring" class="review-plan-card__interval">
            / {{ selectedPlanObj.recurring.interval }}
          </span>
        </div>
      </div>
    </div>
    
    <!-- Actions -->
    <div class="review-actions">
      <themed-button
        type="button"
        variant="secondary"
        @click="$emit('prev')"
        :disabled="loading"
      >
        Back
      </themed-button>
      <themed-button
        type="button"
        variant="default"
        :disabled="loading"
        @click="redirectToCheckout"
      >
        <span v-if="loading">Redirecting...</span>
        <span v-else>Proceed for payment</span>
      </themed-button>
    </div>
    
    <div v-if="checkoutError" class="review-error">{{ checkoutError }}</div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import ThemedButton from '../ThemedButton.vue';
import ThemedTable from '../ThemedTable.vue';
import { formatCellNumber, formatCurrencyCents } from '../../../../utils/formatters';

export default {
  name: 'ReviewStep',
  components: {
    ThemedButton,
    ThemedTable
  },
  props: {
    client: Object,
    user: Object,
    plans: Array,
    selectedPlan: [String, Number],
    states: { type: Array, default: () => [] },
    countries: { type: Array, default: () => [] }
  },
  data() {
    return {
      loading: false,
      checkoutError: '',
      localStates: [],
      localCountries: [],
    };
  },
  computed: {
    selectedPlanObj() {
      return this.plans.find(p => p.id === this.selectedPlan) || {};
    },
    stateName() {
      const code = this.client?.state;
      const states = this.states.length ? this.states : this.localStates;
      return states.find(s => s.state === code)?.name || code || '';
    },
    countryName() {
      const id = this.client?.country_id;
      const countries = this.countries.length ? this.countries : this.localCountries;
      return countries.find(c => c.id == id)?.name || '';
    },
    clientInfoColumns() {
      return [
        { field: 'label', label: 'Field', type: 'text' },
        { field: 'value', label: 'Value', type: 'text' }
      ];
    },
    clientFieldsData() {
      return [
        { label: 'Name', value: this.client?.name || '' },
        { label: 'Email', value: this.user?.email || '' },
        { label: 'Cell', value: this.formatCellNumber(this.user?.cell) || '' },
        { label: 'Address', value: this.client?.address || '' },
        { label: 'City', value: this.client?.city || '' },
        { label: 'State', value: this.stateName },
        { label: 'Country', value: this.countryName },
        { label: 'Postal Code', value: this.client?.postal_code || '' },
      ];
    },
  },
  mounted() {
    if (!this.states.length || !this.countries.length) {
      axios.get('/api/address-info').then(({data}) => {
        this.localStates = data.states || [];
        this.localCountries = data.countries || [];
      });
    }
  },
  methods: {
    formatCurrencyCents,
    formatCellNumber,
    async redirectToCheckout() {
      if (this.loading) return;
      this.loading = true;
      this.checkoutError = '';
      try {
        if (!this.client?.id || !this.user?.id) {
          this.checkoutError = 'Client or user information is missing.';
          return;
        }
        const items = [{ price: this.selectedPlan, quantity: 1 }];
        const { data } = await axios.post('/create-checkout-session', {
          client_id: this.client.id,
          user_id: this.user.id,
          items
        });
        const stripe = await loadStripe(window.STRIPE_PUBLISHABLE_KEY);
        await stripe.redirectToCheckout({ sessionId: data.id });
      } catch (err) {
        this.checkoutError = err.response?.data?.error || 'Could not start checkout.';
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>
